// MyMLMIncomeCredits.jsx
import React, { useState, useMemo } from "react";
import { useNavigate, Link, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";
import adimage1 from '../../assets/pageimages/roomanywhere/raindeximg1.png';
import adimage2 from '../../assets/navlogo.png';

const CATEGORY_MAP = {
  direct: { title: "Direct Referral Income Credits", level: "Direct" },
  l1: { title: "Level 1 Referral Income Credits", level: "Level 1" },
  l2: { title: "Level 2 Referral Income Credits", level: "Level 2" },
  l3: { title: "Level 3 Referral Income Credits", level: "Level 3" },
  l4: { title: "Level 4 Referral Income Credits", level: "Level 4" },
  l5: { title: "Level 5 Referral Income Credits", level: "Level 5" },
  l6: { title: "Level 6 Referral Income Credits", level: "Level 6" },
  l7: { title: "Level 7 Referral Income Credits", level: "Level 7" },
  l8: { title: "Level 8 Referral Income Credits", level: "Level 8" },
  onetime: { title: "One Time Referral Income Credits", level: "One Time" },
  franchise: { title: "Franchise Referral Income Credits", level: "Franchise" }
};

const CREDIT_TYPES = [
  "Digital Books",
  "Physical Products",
  "New Subscriptions",
  "Renewals",
  "Services Referral"
];

const PRODUCT_TITLES = [
  "Practical Thinking Mathematics",
  "Entrepreneurship Starter Kit",
  "Innovation Thinking Toolkit",
  "Digital Productivity System",
  "Knowledge Builder Series",
  "Startup Thinking Course"
];

const PERIOD_OPTIONS = [
  "Yesterday",
  "Today",
  "Current Week",
  "Current Month",
  "Current Year",
  "Last Week",
  "Last Month",
  "Last 3 Months",
  "Last 6 Months",
  "Last 12 Months",
  "Lifetime",
  "Custom"
];

const generateDummyData = () => {
  const data = [];
  const start = new Date();
  start.setFullYear(start.getFullYear() - 2);

  for (let i = 0; i < 1000; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i * 5);

    data.push({
      id: i + 1,
      title: PRODUCT_TITLES[i % PRODUCT_TITLES.length],
      type: CREDIT_TYPES[i % CREDIT_TYPES.length],
      credits: Math.floor(Math.random() * 80) + 10,
      date: d.toISOString().split("T")[0]
    });
  }
  return data;
};

const applyPeriodFilter = (data, period, startDate, endDate) => {
  const today = new Date();
  return data.filter((item) => {
    const d = new Date(item.date);
    switch (period) {
      case "Yesterday": {
        const y = new Date();
        y.setDate(today.getDate() - 1);
        return d.toDateString() === y.toDateString();
      }
      case "Today":
        return d.toDateString() === today.toDateString();
      case "Current Week": {
        const start = new Date();
        start.setDate(today.getDate() - today.getDay());
        return d >= start;
      }
      case "Current Month":
        return d.getMonth() === today.getMonth() &&
               d.getFullYear() === today.getFullYear();
      case "Current Year":
        return d.getFullYear() === today.getFullYear();
      case "Last Month": {
        const last = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        return d.getMonth() === last.getMonth() &&
               d.getFullYear() === last.getFullYear();
      }
      case "Last 3 Months": {
        const s = new Date();
        s.setMonth(today.getMonth() - 3);
        return d >= s;
      }
      case "Last 6 Months": {
        const s = new Date();
        s.setMonth(today.getMonth() - 6);
        return d >= s;
      }
      case "Last 12 Months": {
        const s = new Date();
        s.setFullYear(today.getFullYear() - 1);
        return d >= s;
      }
      case "Custom":
        if (!startDate || !endDate) return true;
        return d >= new Date(startDate) && d <= new Date(endDate);
      default:
        return true;
    }
  });
};

const MyMLMIncomeCredits = () => {
  const { level } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authState = useSelector((state) => state.authReducer);
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showSample, setShowSample] = useState(false);
  const [period, setPeriod] = useState("Yesterday");
  const [type, setType] = useState("Digital Books");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const { title: heading} =
    CATEGORY_MAP[level] || {
      title: "Referral Income Credits",
      level: "Unknown"
    };

  const sampleData = useMemo(() => generateDummyData(), []);
  const filteredData = useMemo(() => {
    let data = applyPeriodFilter(sampleData, period, customStart, customEnd);
    data = data.filter((item) => item.type === type);
    return data;
  }, [sampleData, period, type, customStart, customEnd]);

  const totalCredits = useMemo(() =>
    filteredData.reduce((sum, item) => sum + item.credits, 0),
    [filteredData]
  );

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-black text-gray-100 [&_p]:text-gray-100 flex flex-col">
      <header className="bg-black border-b border-blue-400 px-4 py-3 flex justify-between">
        <div>
          <div className="text-yellow-200 font-bold text-lg">
            My ValueShare Income Credits
          </div>

          <div className="text-xs text-gray-300">
            Think5Whys Micro Digital Income Initiative
          </div>
        </div>

        <div className="hidden md:flex items-center gap-3">
          {isLoggedIn ? (
            <>
              <div className="text-xs lg:text-sm text-teal-300">{userName}</div>
              <button
                onClick={handleLogout}
                className="w-8 mt-2 px-2 py-1 rounded-md bg-red-600 hover:bg-red-700 transition"
                title="Logout"
              >
                ⏻
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => navigate("/login")}
                className="w-8 px-2 py-1 rounded-full border border-gray-700 text-sm text-gray-200 hover:bg-gray-800"
              >
                🔐 
              </button>
            </>
          )}
        </div>
        <button
          onClick={() => setIsMobileMenuOpen((s) => !s)}
          className="md:hidden w-8 h-8 flex items-center justify-center text-2xl text-teal-300"
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? "×" : "☰"}
        </button>
      </header>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-gray-900 border-b border-gray-800 px-4 py-3">
          {isLoggedIn ? (
            <div className="flex flex-col items-center gap-3">
              <div className="text-teal-300 text-xs lg:text-sm font-medium">{userName}</div>
                <div className="flex gap-3">
                  <button
                    onClick={handleLogout}
                    className="p-2 rounded-md bg-red-600 hover:bg-red-700"
                  >
                    ⏻
                  </button>
                </div>
            </div>
          ) : (
            <div className="flex justify-center gap-6">
              <Link to="/login" className="text-teal-300">🔐</Link>
            </div>
          )}
        </div>
      )}
      <div
        className="mt-4 p-3 border-2 border-dashed border-gray-500 rounded-lg bg-gray-900 text-gray-300 cursor-pointer hover:bg-gray-800 transition-all flex items-center gap-3"
        onClick={() => navigate("/roomanywhere")}
        >
            <div className="flex-shrink-0 w-20 h-20 rounded overflow-hidden bg-gray-700">
                <img
                src={adimage1} 
                alt="Ad"
                className="w-full h-full object-cover"
                />
            </div>
            <div className="flex flex-col text-left">
                <p className="text-xs uppercase text-gray-400 mb-1">Think5Whys Ad</p>
                <p className="font-semibold text-lg"><span className="text-yellow-200">RoomAnywhere</span>: Make Indoor and Outdoor Room (Patent Applied)</p>
                <p className="text-sm mt-1">Click to see more!</p>
            </div>
      </div>  

      <div className="text-center mt-4">
        <h1 className="text-xl font-bold text-yellow-200">{heading}</h1>
        <p className="text-sm text-gray-400 mt-1">
          Level / Category: <span className="text-teal-300">{level}</span> 
        </p>
      </div>

      <div className="flex justify-center gap-4 mt-4">
        <button
          disabled
          className="bg-gray-700 text-gray-400 px-4 py-2 rounded"
        >
          My Credit
        </button>
        <button
          onClick={() => setShowSample(true)}
          className="bg-teal-600 px-4 py-2 rounded"
        >
          Sample Credit
        </button>
      </div>

      {showSample && (
        <div className="mt-6 flex flex-wrap gap-4 justify-center">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="bg-gray-800 p-2 rounded h-12"
          >
            {PERIOD_OPTIONS.map((p) => (
              <option key={p}>{p}</option>
            ))}
          </select>
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="bg-gray-800 p-2 rounded h-12"
          >
            {CREDIT_TYPES.map((t) => (
              <option key={t}>{t}</option>
            ))}
          </select>

          {period === "Custom" && (
            <>
              <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="bg-gray-800 p-2 rounded"
              />
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="bg-gray-800 p-2 rounded"
              />
            </>
          )}
        </div>
      )}

      {showSample && (
        <div className="mt-6 px-4 max-w-4xl mx-auto w-full">
          <div className="bg-gray-900 rounded p-4">
            <div className="mb-4 text-teal-300 flex justify-between">
              <span>Showing {filteredData.length} Sample Credits</span>
              <span className="text-yellow-200 font-semibold">Total Credits ₹: {totalCredits}
              </span>
            </div>

            <div className="space-y-2">
              {filteredData.map((item) => (
                <div
                  key={item.id}
                  className="flex justify-between bg-black border border-gray-700 p-3 rounded"
                >
                  <div>
                    <div className="text-sm">
                      {item.title}
                    </div>
                    <div className="text-xs text-gray-400">
                      {item.type} • {item.date}
                    </div>
                  </div>
                  <div className="text-yellow-200 font-semibold">
                    {item.credits} ₹
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

     <div
        className="mt-4 p-3 border-2 border-dashed border-gray-500 rounded-lg bg-gray-900 text-gray-300 cursor-pointer hover:bg-gray-800 transition-all flex items-center gap-3"
        onClick={() => window.open("https://www.think5whys.com", "_blank")}
        >
        <div className="flex-shrink-0 w-16 h-16 bg-gray-700 rounded overflow-hidden">
            <img
                src={adimage2}
                alt="Ad"
                className="w-full h-full object-cover"
            />
            </div>

            <div className="flex flex-col text-left">
            <p className="text-xs uppercase text-gray-400 mb-1">Google Ad</p>
            <p className="font-semibold text-lg">Think5Whys: Your Platform for Transformation</p>
            <p className="text-sm mt-1">Click to see more!</p>
        </div>
      </div>         
      <footer className="bg-black border-t border-blue-400 text-white py-4">
        <div className="mx-auto px-3 flex flex-col lg:flex-row items-center justify-between gap-3">
          <div className="text-sm text-yellow-200">© 2026 Think5Whys Global DeepTech Solutions.</div>
          <div className="flex flex-wrap gap-4 text-sm">
            <Link to="/about-us" className="hover:text-teal-300">About</Link>
            <Link to="/privacy-policy" className="hover:text-teal-300">Privacy Policy</Link>
            <Link to="/terms-of-use" className="hover:text-teal-300">Terms of Use</Link>
            <Link to="/contact-us" className="hover:text-teal-300">Contact</Link>
          </div>
        </div>
      </footer>    
    </div>
  );
};

export default MyMLMIncomeCredits;